/**
 * Profile reads and writes, against the Convex user document.
 *
 * Replaces `GET/PATCH /api/user/profile`, which existed only because the
 * browser could not reach Supabase under Clerk — the api-server had to make the
 * call with a service-role key. Convex resolves the caller from `ctx.auth`, so
 * the client talks to it directly and the endpoints are gone.
 *
 * `role` is deliberately not writable here; plan changes belong to the Stripe
 * webhook.
 */
import { api } from '@/convex/_generated/api'
import type { Doc } from '@/convex/_generated/dataModel'
import { convex, whenConvexReady } from '@/lib/convex-client'

export interface ProfileData {
  id: string
  email: string
  name: string | null
  avatar_url: string | null
  updated_at: string
  role?: string
  has_seen_onboarding?: boolean
  has_seen_notification_prompt?: boolean
}

function toProfile(doc: Doc<'users'>): ProfileData {
  return {
    id: doc.clerkUserId,
    email: doc.email,
    name: doc.name ?? null,
    avatar_url: doc.avatarUrl ?? null,
    updated_at: new Date(doc._creationTime).toISOString(),
    role: doc.role,
    has_seen_onboarding: doc.hasSeenOnboarding,
    has_seen_notification_prompt: doc.hasSeenNotificationPrompt,
  }
}

export async function fetchProfile(): Promise<ProfileData | null> {
  await whenConvexReady()
  const doc = await convex.query(api.users.current, {})
  return doc ? toProfile(doc) : null
}

/**
 * Only the local flags. `name` and `avatar_url` are Clerk's — they arrive via
 * `users.ensureUser` and are not writable from here, so a caller cannot set a
 * value that the next page load would quietly overwrite.
 */
export async function updateProfile(updates: {
  has_seen_onboarding?: boolean
  has_seen_notification_prompt?: boolean
}): Promise<ProfileData> {
  await whenConvexReady()

  const doc = await convex.mutation(api.users.updateProfile, {
    hasSeenOnboarding: updates.has_seen_onboarding,
    hasSeenNotificationPrompt: updates.has_seen_notification_prompt,
  })

  return toProfile(doc)
}
